import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../api/api';
import Navbar from '../components/Navbar';
import CoverageEditor from '../components/CoverageEditor';
import TariffEditor from '../components/TariffEditor';
import SuggestInput from '../components/SuggestInput';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import PasswordStrengthIndicator, { validatePassword } from '../components/PasswordStrengthIndicator';
import { CheckCircle, IdentificationBadge, User as UserIcon, ClockCounterClockwise, MapPin, CurrencyEur } from '@phosphor-icons/react';
import { toast } from 'sonner';

const RegisterProfessionalWizard = ({ embedded = false }) => {
  const { user, login, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [statusLoading, setStatusLoading] = useState(false);

  // Step 1
  const [account, setAccount] = useState({ name: '', email: '', password: '' });

  // Step 2
  const [professions, setProfessions] = useState([]);
  const [regions, setRegions] = useState([]);
  const [provinces, setProvinces] = useState([]);
  const [comuneOk, setComuneOk] = useState(null);
  const [specOptions, setSpecOptions] = useState([]);
  const [specs, setSpecs] = useState([]);
  const [profile, setProfile] = useState({
    profession_code: '',
    specializations: '',
    bio: '',
    region: '',
    province: '',
    city: '',
    vat_number: '',
    business_name: '',
  });

  // Fetch professions when we reach step 2
  useEffect(() => {
    if (step === 2 && professions.length === 0) {
      api.get('/api/professions').then((r) => setProfessions(r.data)).catch(() => {});
      api.get('/api/geo/italian-regions').then((r) => setRegions(r.data || [])).catch(() => {});
    }
  }, [step, professions.length]);

  // Province della regione scelta
  useEffect(() => {
    if (!profile.region) { setProvinces([]); return; }
    api.get(`/api/geo/provinces?region=${encodeURIComponent(profile.region)}`)
      .then((r) => setProvinces(r.data || [])).catch(() => setProvinces([]));
  }, [profile.region]);

  // Specializzazioni suggerite in base alla professione scelta
  useEffect(() => {
    if (!profile.profession_code) { setSpecOptions([]); return; }
    api.get('/api/services-catalog')
      .then((r) => setSpecOptions((r.data || [])
        .filter((x) => (x.profession_codes || []).includes(profile.profession_code))
        .map((x) => x.name)))
      .catch(() => setSpecOptions([]));
  }, [profile.profession_code]);

  const fetchComuni = React.useCallback(async (q) => {
    const r = await api.get(`/api/geo/comuni?province=${encodeURIComponent(profile.province || '')}&q=${encodeURIComponent(q)}&limit=8`);
    return r.data.items || [];
  }, [profile.province]);

  const toggleSpec = (name) => setSpecs((cur) => (
    cur.includes(name) ? cur.filter((x) => x !== name) : [...cur, name]
  ));

  // If already logged in as pro, resume at the right step
  useEffect(() => {
    if (authLoading) return;
    if (user?.role === 'professional') {
      setStatusLoading(true);
      api.get('/api/professionals/me/registration-status').then((r) => {
        const s = r.data.step;
        if (s === 'approved' && !embedded) { navigate('/professional-dashboard'); return; }
        if (s === 'pending_approval' || s === 'approved') {
          if (!embedded) { navigate('/professional/completa-profilo', { replace: true }); return; }
          api.get('/api/professionals/me/onboarding')
            .then((o) => {
              if (o.data.needs_coverage) setStep(3);
              else if (o.data.needs_tariff) setStep(4);
              else setStep(5);
            })
            .catch(() => setStep(5));
          return;
        }
        if (!embedded) { navigate('/professional/completa-profilo', { replace: true }); return; }
        setStep(2);
      }).finally(() => setStatusLoading(false));
    } else if (user && user.role !== 'professional') {
      toast.error('Hai già un account non-professionista');
      navigate('/');
    }
  }, [user, authLoading, navigate]);

  const submitStep1 = async (e) => {
    e.preventDefault();
    const { isValid } = validatePassword(account.password);
    if (!isValid) { toast.error('Password non valida'); return; }
    if (!account.name || !account.email) { toast.error('Compila tutti i campi'); return; }
    setLoading(true);
    try {
      const r = await api.post('/api/auth/register', { ...account, role: 'professional' });
      login(r.data.token, r.data.user);
      toast.success('Account creato! Ora completa il profilo professionale.');
      setStep(2);
      navigate('/professional/completa-profilo', { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore registrazione');
    } finally { setLoading(false); }
  };

  const submitStep2 = async (e) => {
    e.preventDefault();
    if (!profile.profession_code) { toast.error('Seleziona la professione'); return; }
    if (!profile.bio || profile.bio.length < 20) { toast.error('Bio troppo breve (min 20 caratteri)'); return; }
    if (!profile.region || !profile.province) { toast.error('Seleziona regione e provincia'); return; }
    if (!profile.city || !comuneOk) { toast.error('Scegli il comune dall\'elenco dei suggerimenti'); return; }
    if (specOptions.length > 0 && specs.length === 0) { toast.error('Seleziona almeno una specializzazione'); return; }

    setLoading(true);
    try {
      const payload = {
        profession_code: profile.profession_code,
        specializations: specs.length ? specs : [profile.profession_code],
        bio: profile.bio,
        hourly_rate: 0, // il prezzo si definisce nel tariffario per prestazione
        location: `${profile.city}, ${profile.region}`,
        city: profile.city,
        comune: profile.city,
        province: profile.province,
        region: profile.region,
        vat_number: profile.vat_number || null,
        business_name: profile.business_name || null,
      };
      await api.post('/api/professionals/profile', payload);
      toast.success('Profilo salvato. Ora imposta la zona di copertura.');
      setStep(3);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore salvataggio profilo');
    } finally { setLoading(false); }
  };

  if (authLoading || statusLoading) {
    return (
      <div className={embedded ? '' : 'min-h-screen bg-background'}>
        {!embedded && <Navbar />}
        <div className="max-w-3xl mx-auto px-4 py-20 text-center">Caricamento…</div>
      </div>
    );
  }

  return (
    <div className={embedded ? '' : 'min-h-screen bg-background'}>
      {!embedded && <Navbar />}
      <div className="max-w-3xl mx-auto px-4 py-12" data-testid="register-pro-wizard">
        <div className="mb-8">
          <h1 className="font-heading font-semibold text-4xl mb-2">
            {embedded ? 'Completa il tuo profilo' : 'Diventa Professionista'}
          </h1>
          <p className="text-foreground/70">
            Account, profilo, zona di copertura e tariffario: completa i passaggi per iniziare a
            ricevere le richieste della tua zona.
          </p>
        </div>

        {/* Stepper */}
        <div className="flex items-center gap-2 mb-8" data-testid="wizard-stepper">
          {[
            { n: 1, label: 'Account', icon: UserIcon },
            { n: 2, label: 'Profilo', icon: IdentificationBadge },
            { n: 3, label: 'Zone', icon: MapPin },
            { n: 4, label: 'Tariffario', icon: CurrencyEur },
            { n: 5, label: 'In attesa', icon: ClockCounterClockwise },
          ].map(({ n, label, icon: Icon }, i) => {
            const active = step === n;
            const done = step > n;
            return (
              <React.Fragment key={n}>
                <div className={`flex items-center gap-2 px-4 py-2 rounded-full ${active ? 'bg-primary text-primary-foreground' : done ? 'bg-sky-100 text-sky-800' : 'bg-muted text-foreground/60'}`}>
                  {done ? <CheckCircle size={18} weight="fill" /> : <Icon size={18} weight="duotone" />}
                  <span className="text-sm font-medium">{label}</span>
                </div>
                {i < 2 && <div className="flex-1 h-px bg-border" />}
              </React.Fragment>
            );
          })}
        </div>

        {/* Step 1 */}
        {step === 1 && (
          <Card className="neumorphism rounded-2xl p-8" data-testid="step1-card">
            <h2 className="font-semibold text-2xl mb-4">1. Crea il tuo account</h2>
            <form onSubmit={submitStep1} className="space-y-5">
              <div className="space-y-2">
                <Label>Nome completo</Label>
                <Input
                  placeholder="Es. Dott.ssa Anna Rossi"
                  value={account.name}
                  onChange={(e) => setAccount((a) => ({ ...a, name: e.target.value }))}
                  required
                  data-testid="wiz-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Email professionale</Label>
                <Input
                  type="email"
                  value={account.email}
                  onChange={(e) => setAccount((a) => ({ ...a, email: e.target.value }))}
                  required
                  data-testid="wiz-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Password</Label>
                <Input
                  type="password"
                  value={account.password}
                  onChange={(e) => setAccount((a) => ({ ...a, password: e.target.value }))}
                  required
                  data-testid="wiz-password-input"
                />
                <PasswordStrengthIndicator password={account.password} />
              </div>
              <Button type="submit" className="w-full rounded-full" disabled={loading} data-testid="wiz-step1-submit">
                {loading ? 'Creazione…' : 'Continua al profilo'}
              </Button>
              <div className="text-center text-sm text-foreground/70">
                Hai già un account?{' '}
                <Link to="/login" className="text-primary font-medium">Accedi</Link>
              </div>
            </form>
          </Card>
        )}

        {/* Step 2 */}
        {step === 2 && (
          <Card className="neumorphism rounded-2xl p-8" data-testid="step2-card">
            <h2 className="font-semibold text-2xl mb-4">2. Il tuo profilo professionale</h2>
            <form onSubmit={submitStep2} className="space-y-5">
              <div className="space-y-2">
                <Label>Professione</Label>
                <Select
                  value={profile.profession_code}
                  onValueChange={(v) => setProfile((p) => ({ ...p, profession_code: v }))}
                >
                  <SelectTrigger data-testid="wiz-profession-select">
                    <SelectValue placeholder="Seleziona la tua professione" />
                  </SelectTrigger>
                  <SelectContent>
                    {professions.map((p) => (
                      <SelectItem key={p.code} value={p.code}>{p.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Specializzazioni <span className="text-xs text-foreground/50">(seleziona le prestazioni che svolgi)</span></Label>
                {!profile.profession_code ? (
                  <p className="text-xs text-foreground/50">Scegli prima la professione per vedere l'elenco.</p>
                ) : specOptions.length === 0 ? (
                  <p className="text-xs text-foreground/50">Nessuna specializzazione a catalogo per questa professione.</p>
                ) : (
                  <div className="flex flex-wrap gap-1.5" data-testid="wiz-specs-options">
                    {specOptions.map((name) => (
                      <button
                        key={name} type="button" onClick={() => toggleSpec(name)}
                        className={`px-3 py-1.5 rounded-full text-xs border transition-all ${specs.includes(name)
                          ? 'bg-primary text-white border-primary'
                          : 'bg-white text-foreground/70 border-border hover:border-primary/50'}`}
                        data-testid={`wiz-spec-${name}`}
                      >
                        {name}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div className="space-y-2">
                <Label>Bio (min 20 caratteri)</Label>
                <Textarea
                  rows={4}
                  placeholder="Racconta la tua esperienza e cosa offri…"
                  value={profile.bio}
                  onChange={(e) => setProfile((p) => ({ ...p, bio: e.target.value }))}
                  required
                  data-testid="wiz-bio-input"
                />
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>P.IVA (opzionale)</Label>
                  <Input
                    value={profile.vat_number}
                    onChange={(e) => setProfile((p) => ({ ...p, vat_number: e.target.value }))}
                    data-testid="wiz-vat-input"
                  />
                </div>
              </div>
              <div className="grid md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Regione</Label>
                  <Select
                    value={profile.region}
                    onValueChange={(v) => { setProfile((p) => ({ ...p, region: v, province: '', city: '' })); setComuneOk(null); }}
                  >
                    <SelectTrigger data-testid="wiz-region-select"><SelectValue placeholder="Scegli" /></SelectTrigger>
                    <SelectContent position="popper" className="max-h-[300px]">
                      {regions.map((r) => <SelectItem key={r.region} value={r.region}>{r.region}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Provincia</Label>
                  <Select
                    value={profile.province}
                    onValueChange={(v) => { setProfile((p) => ({ ...p, province: v, city: '' })); setComuneOk(null); }}
                    disabled={!profile.region}
                  >
                    <SelectTrigger data-testid="wiz-province-select"><SelectValue placeholder="Scegli" /></SelectTrigger>
                    <SelectContent position="popper" className="max-h-[300px]">
                      {provinces.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Comune</Label>
                  <SuggestInput
                    value={profile.city}
                    onChange={(v) => { setProfile((p) => ({ ...p, city: v })); setComuneOk(null); }}
                    onPick={(c) => { setProfile((p) => ({ ...p, city: c.comune })); setComuneOk(c); }}
                    fetcher={fetchComuni}
                    renderItem={(c) => `${c.comune} (${c.sigla}) — ${c.caps.length} CAP`}
                    placeholder="Es. Milano"
                    disabled={!profile.province}
                    testId="wiz-city-input"
                    emptyHint="Nessun comune con questo nome nella provincia scelta"
                  />
                  {comuneOk && (
                    <p className="text-xs text-emerald-700" data-testid="wiz-comune-ok">
                      Comune confermato · {comuneOk.caps.length} CAP
                    </p>
                  )}
                </div>
              </div>
              <div className="space-y-2">
                <Label>Ragione sociale (opzionale)</Label>
                <Input
                  value={profile.business_name}
                  onChange={(e) => setProfile((p) => ({ ...p, business_name: e.target.value }))}
                  data-testid="wiz-business-input"
                />
              </div>

              <div className="p-4 rounded-lg bg-sky-50 border border-sky-200 text-sm text-sky-900">
                Al completamento potrai pubblicare le tue <strong>promozioni</strong> e ricevere le richieste della tua zona.
                Il profilo sarà visibile solo dopo l'approvazione dell'admin (di solito entro 24h).
              </div>

              <Button type="submit" className="w-full rounded-full" disabled={loading} data-testid="wiz-step2-submit">
                {loading ? 'Invio…' : 'Continua: zona di copertura'}
              </Button>
            </form>
          </Card>
        )}

        {/* Step 3: zona di copertura */}
        {step === 3 && (
          <Card className="neumorphism rounded-2xl p-6" data-testid="step3-coverage">
            <h2 className="font-semibold text-2xl mb-1">3. La tua zona di copertura</h2>
            <p className="text-sm text-foreground/70 mb-4">
              Scegli i comuni e i CAP in cui operi: le richieste di quelle zone arriveranno a te. Dove
              operano dei Centri di Assistenza verrai assegnato automaticamente al Centro.
            </p>
            <CoverageEditor compact onSaved={() => setStep(4)} />
            <div className="flex justify-between gap-3 mt-5">
              <Button variant="ghost" className="rounded-full" onClick={() => setStep(2)} data-testid="wiz-back-2">
                Indietro
              </Button>
              <Button variant="outline" className="rounded-full" onClick={() => setStep(4)} data-testid="wiz-skip-coverage">
                Continua: tariffario
              </Button>
            </div>
          </Card>
        )}

        {/* Step 4: tariffario */}
        {step === 4 && (
          <Card className="neumorphism rounded-2xl p-6" data-testid="step4-tariff">
            <h2 className="font-semibold text-2xl mb-1">4. Il tuo tariffario</h2>
            <p className="text-sm text-foreground/70 mb-4">
              Indica il prezzo delle prestazioni che svolgi. Se non lo compili ora riceverai un
              promemoria via email: senza tariffario il profilo resta incompleto e sei meno visibile.
            </p>
            <TariffEditor professionCode={profile.profession_code} allowedNames={specs} onSaved={() => setStep(5)} />
            <div className="flex justify-between gap-3 mt-5">
              <Button variant="ghost" className="rounded-full" onClick={() => setStep(3)} data-testid="wiz-back-3">
                Indietro
              </Button>
              <Button variant="outline" className="rounded-full" onClick={() => setStep(5)} data-testid="wiz-skip-tariff">
                Lo faccio dopo
              </Button>
            </div>
          </Card>
        )}

        {/* Step 5: in attesa di approvazione */}
        {step === 5 && (
          <Card className="neumorphism rounded-2xl p-10 text-center" data-testid="step3-card">
            <ClockCounterClockwise size={56} className="mx-auto text-primary mb-4" weight="duotone" />
            <h2 className="font-semibold text-2xl mb-2">Profilo completo, in attesa di approvazione</h2>
            <p className="text-foreground/70 mb-6 max-w-md mx-auto">
              Il team VivaLink Italia sta esaminando la tua candidatura. Appena approvata riceverai una
              notifica e inizierai a ricevere le richieste delle tue zone.
            </p>
            <div className="grid md:grid-cols-3 gap-3 max-w-xl mx-auto">
              <Button variant="outline" className="rounded-full" onClick={() => setStep(3)} data-testid="go-coverage-btn">
                Zona di copertura
              </Button>
              <Button variant="outline" className="rounded-full" onClick={() => setStep(4)} data-testid="go-tariff-btn">
                Tariffario
              </Button>
              <Button className="rounded-full" onClick={() => navigate('/professional-dashboard')} data-testid="go-dashboard-btn">
                Vai alla Dashboard
              </Button>
            </div>
          </Card>
        )}

      </div>
    </div>
  );
};

export default RegisterProfessionalWizard;
